import { useState, useEffect, useMemo } from "react";
import { Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

export const SearchDialog = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  const searchItems = useMemo(() => [
    // Pages
    { group: t("search.pages"), label: t("nav.home"), path: "/" },
    { group: t("search.pages"), label: t("nav.viewPlans"), path: "/view-plans" },
    { group: t("search.pages"), label: t("nav.caseStudy"), path: "/reviews" },
    { group: t("search.pages"), label: t("nav.whatAreWeDoing"), path: "/areas-of-expertise" },
    // Categories
    { group: t("search.categories"), label: t("nav.fitnessEnthusiasts"), path: "/categories/normal-people" },
    { group: t("search.categories"), label: t("nav.eliteAthletes"), path: "/categories/elite-athletes" },
    { group: t("search.categories"), label: t("nav.footballTraining"), path: "/categories/football-training" },
    // Services
    { group: t("search.services"), label: t("services.evaluation"), path: "/services/evaluation" },
    { group: t("search.services"), label: t("services.fatAnalysis"), path: "/services/fat-analysis" },
    { group: t("search.services"), label: t("services.footballTraining"), path: "/services/football-training" },
    { group: t("search.services"), label: t("services.fullBodyWeight"), path: "/services/full-body-weight" },
    { group: t("search.services"), label: t("services.gymTraining"), path: "/services/gym-training" },
    { group: t("search.services"), label: t("services.lactateTest"), path: "/services/lactate-test" },
    { group: t("search.services"), label: t("services.nutrition"), path: "/services/nutrition" },
    { group: t("search.services"), label: t("services.performanceDiagnostic"), path: "/services/performance-diagnostic" },
    { group: t("search.services"), label: t("services.psychology"), path: "/services/psychology" },
    { group: t("search.services"), label: t("services.vo2Max"), path: "/services/vo2-max" },
    // Account
    { group: t("search.account"), label: t("auth.login"), path: "/login" },
    { group: t("search.account"), label: t("nav.dashboard"), path: "/dashboard" },
    { group: t("search.account"), label: t("nav.settings"), path: "/settings" },
    { group: t("search.account"), label: t("checkout.title"), path: "/checkout" },
  ], [t]);

  const groups = useMemo(() => {
    const map = new Map<string, typeof searchItems>();
    searchItems.forEach((item) => {
      if (!map.has(item.group)) map.set(item.group, []);
      map.get(item.group)!.push(item);
    });
    return map;
  }, [searchItems]);

  const handleSelect = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen(true)}
        className="text-primary hover:text-primary/80"
        aria-label={t("search.placeholder")}
      >
        <Search className="h-5 w-5" />
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder={t("search.placeholder")} />
        <CommandList>
          <CommandEmpty>{t("search.noResults")}</CommandEmpty>
          {Array.from(groups.entries()).map(([group, items]) => (
            <CommandGroup key={group} heading={group}>
              {items.map((item) => (
                <CommandItem
                  key={item.path}
                  value={item.label}
                  onSelect={() => handleSelect(item.path)}
                >
                  {item.label}
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
};
